/**
 * Utilidades de "Otros conceptos" + "Puntos de red" para TRASLADOS.
 *
 * Los traslados reutilizan los componentes de visitas (OtrosConceptosVisita,
 * PuntosDeRedVisita) pero con el catálogo filtrado por flujo TRASLADO
 * (`usePreciosConceptosVisita('TRASLADO')`) y con su propia regla de aprobación:
 * si el técnico agrega/cambia conceptos cuando el traslado YA está iniciado, el
 * operador de Activaciones debe aprobarlos antes de poder finalizar.
 *
 * Todo se persiste en `agendamiento.observaciones` del traslado (Django), igual
 * que en visitas, sin tocar el micro ni el flujo de instalaciones:
 *  - otros_conceptos / otros_conceptos_historial
 *  - puntos_red / puntos_red_historial
 *  - conceptos_aprobacion: { estado, solicitado_por, solicitado_fecha,
 *                           aprobado_por, aprobado_fecha }
 */
import type { ConceptoVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import type { PuntoRed } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/PuntosDeRedVisita";

export type OrigenCambioConceptos = "operaciones" | "tecnico" | "activaciones";

export type EntradaHistorialConceptos<T> = {
  usuario: string;
  fecha: string;
  origen: OrigenCambioConceptos;
  anterior: T[];
  nuevo: T[];
};

export type EstadoAprobacionConceptos = "PENDIENTE" | "APROBADO";

export type AprobacionConceptosTraslado = {
  estado: EstadoAprobacionConceptos;
  solicitado_por?: string;
  solicitado_fecha?: string;
  aprobado_por?: string;
  aprobado_fecha?: string;
};

/** Nombre legible del usuario autenticado (username o nombre y apellido). */
export function nombreUsuario(user: any): string {
  return (
    user?.username ||
    [user?.nombre, user?.apellido].filter(Boolean).join(" ").trim() ||
    user?.razon_social ||
    "—"
  );
}

/** Observaciones del agendamiento como objeto (acepta string JSON o null). */
export function obsComoObjeto(obs: any): Record<string, any> {
  if (!obs) return {};
  if (typeof obs === "string") {
    try {
      const j = JSON.parse(obs);
      return j && typeof j === "object" ? j : {};
    } catch {
      return {};
    }
  }
  return typeof obs === "object" ? obs : {};
}

function listaDe<T>(v: any): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/** ¿Cambió la lista? (comparación estructural, sin importar fotos). */
export function cambiaronItems<T>(a: T[] | null | undefined, b: T[] | null | undefined): boolean {
  return JSON.stringify(listaDe<T>(a)) !== JSON.stringify(listaDe<T>(b));
}

/**
 * Anexa una entrada de trazabilidad si la lista cambió. Devuelve el historial
 * (previo o previo + nueva entrada). Reutilizable para conceptos y puntos de red.
 */
export function trazaCambioLista<T>(
  anterior: T[] | null | undefined,
  nuevo: T[] | null | undefined,
  historialPrevio: any,
  usuario: string,
  origen: OrigenCambioConceptos
): EntradaHistorialConceptos<T>[] {
  const prev = listaDe<EntradaHistorialConceptos<T>>(historialPrevio);
  if (!cambiaronItems(anterior, nuevo)) return prev;
  return [
    ...prev,
    {
      usuario,
      fecha: new Date().toISOString(),
      origen,
      anterior: listaDe<T>(anterior),
      nuevo: listaDe<T>(nuevo),
    },
  ];
}

export const trazaOtrosConceptos = (
  anterior: ConceptoVisita[] | null | undefined,
  nuevo: ConceptoVisita[] | null | undefined,
  historialPrevio: any,
  usuario: string,
  origen: OrigenCambioConceptos
) => trazaCambioLista<ConceptoVisita>(anterior, nuevo, historialPrevio, usuario, origen);

export const trazaPuntosRed = (
  anterior: PuntoRed[] | null | undefined,
  nuevo: PuntoRed[] | null | undefined,
  historialPrevio: any,
  usuario: string,
  origen: OrigenCambioConceptos
) => trazaCambioLista<PuntoRed>(anterior, nuevo, historialPrevio, usuario, origen);

/** Lee el estado de aprobación de conceptos del traslado (null si nunca aplicó). */
export function aprobacionDe(obs: any): AprobacionConceptosTraslado | null {
  const o = obsComoObjeto(obs);
  const a = o?.conceptos_aprobacion;
  if (!a || typeof a !== "object") return null;
  return a as AprobacionConceptosTraslado;
}

/** ¿Hay conceptos/puntos de red del técnico pendientes de aprobación por Activaciones? */
export function conceptosPendientesAprobacion(obs: any): boolean {
  return aprobacionDe(obs)?.estado === "PENDIENTE";
}

/**
 * Marca como PENDIENTE la aprobación (el técnico cambió conceptos/puntos con el
 * traslado ya iniciado). Conserva quién lo solicitó y cuándo.
 */
export function marcarAprobacionPendiente(usuario: string): AprobacionConceptosTraslado {
  return {
    estado: "PENDIENTE",
    solicitado_por: usuario,
    solicitado_fecha: new Date().toISOString(),
  };
}

/** Marca como APROBADO (operador de Activaciones). */
export function marcarAprobado(previa: AprobacionConceptosTraslado | null, usuario: string): AprobacionConceptosTraslado {
  return {
    ...(previa || { estado: "PENDIENTE" }),
    estado: "APROBADO",
    aprobado_por: usuario,
    aprobado_fecha: new Date().toISOString(),
  };
}

/** El traslado se considera INICIADO cuando el técnico registró la hora de inicio. */
export function trasladoIniciado(ordenTrabajo: any): boolean {
  return !!ordenTrabajo?.fecha_hora_inicio_instalacion;
}

/** Resumen corto de conceptos para chips/listas ("ONT (EFECTIVO)"). */
export function resumenConceptos(items: ConceptoVisita[] | null | undefined): string[] {
  return listaDe<ConceptoVisita>(items).map((c) => `${c.concepto}${c.tipo_pago ? ` (${c.tipo_pago})` : ""}`);
}

/** Total con IVA de conceptos + puntos de red (referencial). */
export function totalConceptos(
  conceptos: ConceptoVisita[] | null | undefined,
  puntos: PuntoRed[] | null | undefined
): number {
  const a = listaDe<ConceptoVisita>(conceptos).reduce((s, c) => s + (Number(c.valor_total) || 0), 0);
  const b = listaDe<PuntoRed>(puntos).reduce((s, p) => s + (Number(p.valor_total) || 0), 0);
  return Math.round((a + b) * 100) / 100;
}
